import type { ReactNode } from "react";
import Link from "next/link";
import { Fire } from "@phosphor-icons/react/dist/ssr";
import { MiraMark } from "@/components/ui/Logo";
import { formatPlazaStartingPrice } from "@/features/plaza/price";
import { publicProfileText } from "@/features/plaza/public-profile";
import type { PlazaAvatar } from "@/features/plaza/types";

function formatFans(value: number) {
  if (value < 1_000) return "";
  if (value < 10_000) return value.toLocaleString("zh-CN");
  const amount = Number((value / 10_000).toFixed(1));
  return `${amount.toLocaleString("zh-CN")}万`;
}

export function PlazaCard({ avatar, actions, detailQuery = "", navigationPrefetch, showPrice = false }: { avatar: PlazaAvatar; actions?: ReactNode; detailQuery?: string; navigationPrefetch?: false; showPrice?: boolean }) {
  const fans = formatFans(avatar.fansCount);
  const titleText = publicProfileText(avatar.title, "未命名形象");
  const title = titleText === "暂无数据" ? "未命名形象" : titleText;
  const href = `/plaza/${encodeURIComponent(avatar.id)}${detailQuery}`;

  return (
    <article className="group overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-line)] bg-[var(--color-raised)] shadow-[var(--shadow-node)] transition duration-300 hover:-translate-y-0.5 hover:border-[var(--color-line-strong)]">
      <Link prefetch={navigationPrefetch} href={href} className="block">
        <div className="relative aspect-[3/4] overflow-hidden bg-[var(--color-surface-3)]">
        {avatar.imageUrl ? (
          // CloudBase returns short-lived HTTPS thumbnails for already-public, online avatars.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatar.imageUrl}
            alt={`${title}形象照`}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="size-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="grid size-full place-items-center bg-[radial-gradient(circle_at_20%_10%,rgba(107,196,224,0.22),transparent_42%),radial-gradient(circle_at_85%_90%,rgba(255,184,119,0.18),transparent_45%),var(--color-surface-3)]">
            <span className="text-[var(--color-faint)]"><MiraMark size={52} /></span>
          </div>
        )}
        {avatar.recommended && (
          <span aria-label="推荐形象" className="absolute right-3 top-3 inline-flex items-center justify-center rounded-full bg-black/55 p-1.5 backdrop-blur-md">
            <Fire size={12} weight="fill" className="text-[var(--color-warm)]" aria-hidden="true" />
          </span>
        )}
        </div>
        <div className="p-3 text-[var(--color-paper)] sm:p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="min-w-0 truncate font-display text-[15px] font-semibold sm:text-base">{title}</h2>
            {fans && <span className="shrink-0 text-[10px] font-normal text-[var(--color-muted)] sm:text-[11px]">{fans}粉丝</span>}
          </div>
          {showPrice && <p className="mt-2 text-xs font-semibold sm:text-sm">{formatPlazaStartingPrice(avatar)}</p>}
        </div>
      </Link>
      {actions}
    </article>
  );
}
